import { useState, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { getStudioPipelineCalendar, updateStudioPipelineCalendar, getStudioPipelineCalendarStudios } from '@sg/services/globalpedia';
import {
  getFormatProjectDatas,
  getSortProjectDatas,
  computeHighlights,
  transformApiDataNew,
  generateMonthColumns,
  getProjectDatasTitleAbbr,
} from '../utils';
import type {
  TSegmentType,
  IPipelineProject,
  IEventTypeFilterOption,
  ISelectedEventInfo,
} from '../components/PipelineCalendar/type';
import {
  IN_DEV_EVENT_TYPES, LIVE_EVENT_TYPES,
} from '../components/PipelineCalendar/const';

export const usePipelineCalendarDataNew = (
  selectedMonth: string,
  studioIdPa?: string,
  segment: TSegmentType = 'in_dev',
  isStudioPage: boolean = false,
  setActiveSegment?: any,
) => {

  const [projects, setProjects] = useState<IPipelineProject[]>([]);
  const allProjectsRef = useRef<IPipelineProject[]>([]);
  const allProjectsRefOrder = useRef<IPipelineProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['all']);
  const [notesContent, setNotesContent] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<ISelectedEventInfo | null>(null);
  const [studioProjectList, setStudioProjectList] = useState<any[]>([]);
  const [projectOrderList, setProjectOrderList] = useState<any[]>([]);

  const monthColumns = useMemo(() => generateMonthColumns(selectedMonth), [selectedMonth]);

  const eventTypeFilters: IEventTypeFilterOption[] = useMemo(() => {
    if (segment === 'live') {
      return LIVE_EVENT_TYPES;
    }
    return IN_DEV_EVENT_TYPES;
  }, [segment]);

  // Derive unique studio and project options from loaded data
  const studioOptions = useMemo(() => {
    const studios = new Set<string>();
    projects.forEach(p => {
      if (p.studio) {
        studios.add(p.studio);
      }
    });
    return Array.from(studios).map(s => ({
      value: s,
      text: s,
    }));
  }, [projects]);

  // Filter projects by cascade filter (game_title leaf IDs), then by event type

  // 只负责调接口 + 转换数据，不修改任何状态，供 fetchData / studio 页首次双接口调用复用
  const fetchOnce = async (selectedMonthValue: string, activeSegment: string) => {
    const selectedMonthStr = selectedMonthValue
      ? moment(selectedMonthValue).format('YYYY-MM')
      : moment().startOf('month').format('YYYY-MM');

    const fetchFun = isStudioPage ? getStudioPipelineCalendarStudios : getStudioPipelineCalendar;

    let newParams = {
      view_type: activeSegment,
      date: selectedMonthStr,
    } as any;

    if (isStudioPage && studioIdPa) {
      newParams = {
        ...newParams,
        studio_id: studioIdPa,
      };
    }

    const resDatas = await fetchFun(newParams);
    const projectOrderDatas = resDatas?.all_project || [];
    const projectOrderListTemp = getSortProjectDatas(projectOrderDatas);
    const allProject = getFormatProjectDatas(projectOrderDatas);
    const projectDatasTitleAbbr = getProjectDatasTitleAbbr(projectOrderDatas);
    const transformed = transformApiDataNew(
      resDatas?.project_timeline,
      projectOrderListTemp,
      projectDatasTitleAbbr,
    );

    return {
      transformed,
      projectOrderListTemp,
      allProject,
      selectedMonthStr,
    };
  };

  // 应用接口返回结果：写入各种状态 / ref，并默认选中当月首个事件
  const applyFetchResult = (
    payload: {
      transformed: IPipelineProject[];
      projectOrderListTemp: any[];
      allProject: any[];
      selectedMonthStr: string;
    },
    isNeedUpdateSelect: boolean,
  ) => {
    const { transformed, projectOrderListTemp, allProject, selectedMonthStr } = payload;

    setProjectOrderList(projectOrderListTemp);
    allProjectsRefOrder.current = projectOrderListTemp;
    setStudioProjectList(allProject);
    allProjectsRef.current = transformed;
    setProjects(transformed);

    // 每次请求接口后，默认第一个项目的当前月份第一个有数据的事件给 setSelectedEvent
    let firstSelectedEvent: ISelectedEventInfo | null = null;
    const firstProj = transformed[0] || {};
    const monthEvents = firstProj?.events?.[selectedMonthStr] || [];
    const firstEventTemp = monthEvents?.find((evt) => !!evt.name);
    const firstEvent = firstEventTemp || monthEvents[0];

    if (firstEvent) {
      firstSelectedEvent = {
        eventName: firstEvent.name,
        projectName: firstProj.project,
        date: firstEvent.date,
        notes: firstEvent.notes || '',
        studioId: String(firstEvent.id || ''),
        type: firstEvent.type || '',
      };
    }
    if (isNeedUpdateSelect) {
      setSelectedEvent(firstSelectedEvent);
    }

    setNotesContent(firstEvent?.note || '');
  };

  const fetchData = async (selectedMonthValue: string, activeSegment: string, isNeedUpdateSelect = true) => {
    setLoading(true);
    try {
      const payload = await fetchOnce(selectedMonthValue, activeSegment);
      applyFetchResult(payload, isNeedUpdateSelect);
    } catch (error) {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Studio 页首次进入：并发请求 in_dev 与 live，根据两边 transformed 是否有数据决定默认 segment
  // - 两边都有 / 两边都无 / in_dev 有 live 无 → 默认 in_dev
  // - in_dev 无数据且 live 有数据 → 默认 live
  const initFetchDataForStudio = async (selectedMonthValue: string) => {
    setLoading(true);
    try {
      const [inDevResult, liveResult] = await Promise.all([
        fetchOnce(selectedMonthValue, 'in_dev'),
        fetchOnce(selectedMonthValue, 'live'),
      ]);

      const inDevHasData = (inDevResult?.transformed?.length || 0) > 0;
      const liveHasData = (liveResult?.transformed?.length || 0) > 0;

      if (!inDevHasData && liveHasData) {
        setActiveSegment?.('live');
        applyFetchResult(liveResult, true);
      } else {
        setActiveSegment?.('in_dev');
        applyFetchResult(inDevResult, true);
      }
    } catch (error) {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedEvent) {
      return;
    }

    try {
      setLoading(true);
      let newParams = {
        project_orders: projectOrderList,
      } as any;
      newParams = {
        ...newParams,
        date: selectedEvent.date,
        view_type: segment,
        note: notesContent,
      };

      if (isStudioPage && studioIdPa) {
        newParams = {
          ...newParams,
          studio_id: studioIdPa,
        };
      }
      await updateStudioPipelineCalendar(newParams);
      // 更新成功后，重新请求接口，刷新数据
      fetchData(selectedMonth, segment, false);
      setSelectedEvent({
        ...selectedEvent,
        notes: notesContent,
      });
    } catch (error) {
      setLoading(false);
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    if (isStudioPage) {
      if (studioIdPa) {
        // Studio 页首次进入：并发请求 in_dev / live，根据哪边有数据默认 segment
        initFetchDataForStudio(selectedMonth);
      }
    } else {
      fetchData(selectedMonth, segment);
    }
  }, [studioIdPa, isStudioPage]);

  const handleFilterChange = (filters: string[]) => {
    setSelectedFilters(filters);

    // ['all'] 或空数组 → 恢复全量项目（不过滤事件）
    if (filters.includes('all') || filters.length === 0) {
      setProjects(allProjectsRef.current);
      return;
    }

    // 按事件类型过滤：每个 project 只保留 events 中 type 匹配的条目
    const filtered = allProjectsRef.current.map((proj: IPipelineProject) => {
      const filteredEvents: Record<string, IPipelineProject['events'][string]> = {};
      Object.entries(proj.events).forEach(([monthKey, monthEvents]) => {
        const matched = (monthEvents || []).filter(
          (evt) => evt.name && filters.includes(evt.type),
        );
        // 有匹配事件则保留（含匹配事件），无匹配则保留空态占位
        if (matched.length > 0) {
          filteredEvents[monthKey] = matched;
        } else {
          // 保留空态占位事件（name 为空的）
          const emptyEvent = (monthEvents || []).find((e) => !e.name);
          filteredEvents[monthKey] = emptyEvent ? [emptyEvent] : [];
        }
      });
      return { ...proj, events: filteredEvents };
    });

    setProjects(filtered);
  };

  const handleActiveSegmentFilterChange = (segmentValue: string) => {
    fetchData(selectedMonth, segmentValue);
    // setSelectedFilters(['all']);
  };
  const handleDateFilterChange = (selectedMonthValue: string) => {
    fetchData(selectedMonthValue, segment);
  };

  // TODO: 实现项目重新排序逻辑
  const handleProjectsReorder = (reorderedProjects: IPipelineProject[]) => {
    const projectOrderListTemp = reorderedProjects.map((project, index) => ({
      game_title: project.project,
      project_order: index + 1,
    }));
    setProjectOrderList(projectOrderListTemp);
    setProjects([...reorderedProjects]);
  };

  const handleProjectsReorderCancel = () => {
    setProjectOrderList([...allProjectsRefOrder.current]);
    setProjects([...allProjectsRef.current]);
  };

  const handleNotesChange = (content: string) => {
    setNotesContent(content);
  };

  return {
    projects,
    allProjects: allProjectsRef.current,
    monthColumns,
    loading,
    eventTypeFilters,
    selectedFilters,
    notesContent,
    studioOptions,
    handleFilterChange,
    handleProjectsReorder,
    handleProjectsReorderCancel,
    handleNotesChange,
    handleActiveSegmentFilterChange,
    handleDateFilterChange,
    selectedEvent,
    setSelectedEvent,
    studioProjectList,
    handleConfirm,
    setNotesContent,
  };
};
